import { isMockServices } from 'src/utils';
import mock from 'src/services/utils/mock';
import { QueryFunction } from '@tanstack/react-query';
import { Document, Page } from '../documents/types';
import { DocumentAssociation, VersionType } from '../documents/enums';
import { getCompatibleAuthTokenHttp } from '../utils/enhancedAuthTokenHttp';
import { addQuery, buildIncludeQuery } from '../utils/queryBuilder';
import { loanDocumentsKeys } from 'src/queries/loans/useLoanDocuments';
import { LoanDocumentsResponse } from 'src/queries/loans/useLoanDocuments';
import { z } from 'zod';

export interface GetLoanDocumentsParams {
  loanId: string;
  includeAssociations?: DocumentAssociation[];
  /** When true, requests export status from the API (e.g. LOS sync). Typically set when the loan has an external LOS id. */
  includeExportStatus?: boolean;
}

export interface GetLoanDocumentsResponse {
  status: string;
  documents: Document[];
  pagesByDocument: { [key: string]: Page[] };
  nextCursor: string | null;
}

export const documentSchema = z
  .object({
    id: z.string(),
    documentTypeConfidence: z.number().optional(),
    documentPages: z.array(
      z
        .object({
          pageId: z.string(),
          pageOrder: z.number(),
        })
        .passthrough(),
    ),
    documentType: z
      .object({
        id: z.string(),
        displayName: z.string(),
      })
      .passthrough(),
  })
  .passthrough();

const GetLoanDocumentsResponseSchema = z
  .object({
    status: z.string(),
    documents: z.array(documentSchema),
    pagesByDocument: z
      .record(
        z.string(),
        z.array(
          z
            .object({
              id: z.string(),
              number: z.number(),
              thumbnailUrl: z.string(),
            })
            .passthrough(),
        ),
      )
      .optional(),
    nextCursor: z.string().nullable(),
  })
  .passthrough();

const getLoanDocuments: QueryFunction<
  LoanDocumentsResponse,
  ReturnType<typeof loanDocumentsKeys.byLoan>,
  string | null
> = async ({ queryKey, pageParam }) => {
  const { loanId, includeAssociations, includeExportStatus } = queryKey[1];

  if (isMockServices()) return await mock(_mockLoanDocuments);

  // Safely build our query string parameters
  let queryParams = buildIncludeQuery<DocumentAssociation>(includeAssociations);
  if (includeExportStatus) {
    queryParams = addQuery(queryParams, 'includeExportStatus', 'true');
  }
  // If useInfiniteQuery passes us a cursor, append it so the API knows where to start
  if (pageParam) queryParams = addQuery(queryParams, 'cursor', pageParam);

  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<GetLoanDocumentsResponse>(
    `/loans/${loanId}/documents/pages${queryParams}`,
    {
      method: 'GET',
      paginate: false, // Turn off internal pagination so React Query can manage it and update the UI in real-time
      limit: 100,
    },
  );

  if (!response.data) throw new Error('Failed to fetch loan documents');

  const validatedData = GetLoanDocumentsResponseSchema.safeParse(response.data);

  if (!validatedData.success) {
    const error = validatedData.error.message;
    console.error(`Get Loan Documents validation failed: ${error}`);
    throw new Error(`Failed to parse documents for loan: ${loanId}.`);
  }

  // NOTE: Casting to unknown is a temporary fix until there is a Refactor for proper zod implementation.
  const rawDocuments =
    (validatedData.data.documents as unknown as Document[]) ?? [];
  const pagesByDocument =
    (validatedData.data.pagesByDocument as unknown as {
      [key: string]: Page[];
    }) ?? {};

  // 3. Apply your processing logic
  const processedDocuments = rawDocuments.map((doc) => {
    const pages = pagesByDocument[doc.id] ?? [];

    for (const page of pages) {
      const documentPage = doc.documentPages?.find(
        (dp) => dp.pageId === page.id,
      );
      page.pageId = documentPage?.pageId ?? '';

      if (documentPage?.pageOrder !== undefined) {
        page.pageOrder = documentPage.pageOrder;
      } else if (page.number !== undefined) {
        console.warn(
          `Missing pageOrder for page ${page.id} in document ${doc.id}. Falling back to page number from original sourcefile`,
        );
        page.pageOrder = page.number;
      } else {
        console.warn(
          `Missing pageOrder and number for page ${page.id} in document ${doc.id}`,
        );
        page.pageOrder = 1;
      }
    }

    pages.sort((a, b) => (a.pageOrder || 0) - (b.pageOrder || 0));
    const rawConfidence = doc.documentTypeConfidence ?? doc.confidence;
    const confidence =
      typeof rawConfidence === 'number' && Number.isFinite(rawConfidence)
        ? rawConfidence
        : 0;

    return {
      ...doc,
      confidence,
      pages,
    };
  });

  return {
    success: response.status === 200 || validatedData.data.status === 'success',
    documents: processedDocuments,
    nextCursor: validatedData.data.nextCursor,
  };
};

const _mockLoanDocuments: LoanDocumentsResponse = {
  success: true,
  documents: [
    {
      id: 'doc-1',
      name: 'Application Form',
      documentTypeId: 'app-form-type',
      confidence: 95,
      versionIdentifier: VersionType.FINAL,
      version: 0,
      pages: [
        {
          id: 'page-1',
          imageUrl: '/mocks/image-01.webp',
          thumbnailUrl: '/mocks/image-01-thumb.webp',
          height: 3300,
          width: 2550,
          dpix: null,
          dpiy: null,
          classification: 'Application',
          classificationConfidence: 95,
          number: 1,
          pdfUrl: '/mocks/doc-01.pdf',
          pageId: 'page-1',
          pageOrder: 1,
        },
      ],
      fields: [],
      documentType: {
        id: 'app-form-type',
        name: 'Application Form',
        displayName: 'Application Form',
        fields: [],
        filenamePrefix: null,
        classificationReviewThreshold: 0,
        extractionReviewThreshold: 0,
        borrowerReviewThreshold: 0,
        highConfThresh: 0,
      },
      documentPages: [
        {
          id: 'page-1',
          documentId: 'doc-1',
          pageId: 'page-1',
          pageOrder: 1,
        },
      ],
      isNew: false,
      isViewed: true,
      isVerified: true,
      userApproved: true,
    },
    {
      id: 'doc-2',
      name: 'Income Statement',
      documentTypeId: 'income-type',
      confidence: 88,
      versionIdentifier: VersionType.FINAL,
      version: 0,
      pages: [
        {
          id: 'page-2',
          imageUrl: '/mocks/image-02.webp',
          thumbnailUrl: '/mocks/image-02-thumb.webp',
          height: 3300,
          width: 2550,
          dpix: null,
          dpiy: null,
          classification: 'Income Statement',
          classificationConfidence: 88,
          number: 1,
          pdfUrl: '/mocks/doc-02.pdf',
          pageId: 'page-2',
          pageOrder: 1,
        },
      ],
      fields: [],
      documentPages: [
        {
          id: 'page-2',
          documentId: 'doc-2',
          pageId: 'page-2',
          pageOrder: 1,
        },
      ],
      isNew: false,
      isViewed: true,
      isVerified: true,
      userApproved: true,
      documentType: {
        id: 'income-type',
        name: 'Income Statement',
        displayName: 'Income Statement',
        fields: [],
        filenamePrefix: null,
        classificationReviewThreshold: 0,
        extractionReviewThreshold: 0,
        borrowerReviewThreshold: 0,
        highConfThresh: 0,
      },
    },
  ],
  nextCursor: null,
};

export default getLoanDocuments;
